// lib/clients/kaizershop.ts
// Client configuration for kaizershop.gr — Greek tech & electronics e-commerce.

import type { ClientConfig } from './types';

// ── Product Categories ──
// Top-level categories stored in `category` field. Sub-paths in `subcategory` array.
// Agent uses top-level for broad matching. Subcategories show in sidebar for filtering.
const PRODUCT_CATEGORIES = [
  // Top-level categories (used in searchProducts categories param).
  //
  // The agent's `resolveCategories` helper looks up these names against the
  // `category` facet in the index — which only contains top-level values
  // (the OpenCart XML parser indexes the level-0 category name per product).
  //
  // Specific subcategories (Ηχεία-Ακουστικά, Θήκες, Φορτιστές, ...) live in
  // the `subcategory[]` array per product, not in `category`. So adding them
  // to this list would make the agent pick them and fail to match — which
  // is worse than the current broad-but-working behavior. Sharper agent
  // targeting requires either parser change (use leaf-most as `category`)
  // or resolveCategories enhancement (match against subcategory too).
  // Tracked as a follow-up; not blocking the demo.
  'Κινητά Τηλέφωνα',
  'Αξεσουάρ',
  'Tablets',
  'Gadgets',
  'Κονσόλες & gaming',
  'Desktop & Laptop',
  'Σπίτι & Γραφείο',
  'Εκθεσιακά',
  'Μεταχειρισμένα',
  'Hot Offers',
];

// ── Product Concepts ──
const PRODUCT_CONCEPTS = [
  // Mobile & Tablets
  'κινητό', 'smartphone', 'tablet', 'iphone', 'samsung galaxy', 'xiaomi redmi',
  // Accessories
  'θήκη', 'θήκη κινητού', 'tempered glass', 'προστασία οθόνης', 'φορτιστής',
  'καλώδιο', 'power bank', 'ακουστικά', 'airpods', 'handsfree',
  // Audio
  'ηχείο', 'bluetooth ηχείο', 'soundbar', 'ακουστικό',
  // Wearables
  'smartwatch', 'έξυπνο ρολόι', 'fitness tracker', 'λουράκι smartwatch',
  // Gaming
  'χειριστήριο', 'gaming mouse', 'gaming keyboard', 'playstation', 'nintendo',
  'παιχνίδι', 'headset gaming',
  // PC
  'οθόνη', 'monitor', 'πληκτρολόγιο', 'ποντίκι', 'σκληρός δίσκος', 'SSD',
  'επεξεργαστής', 'κάρτα γραφικών', 'webcam',
  // Home Appliances
  'καφετιέρα', 'espresso machine', 'μπλέντερ', 'φριτέζα', 'βραστήρας',
  'σκούπα', 'ρομποτική σκούπα', 'σκούπα ρομπότ', 'cordless vacuum', 'dyson', 'τοστιέρα',
  'φούρνος pizza', 'έξυπνη ζυγαριά',
  // Home
  'φωτιστικό', 'βάση τηλεόρασης', 'κάμερα ασφαλείας', 'router',
  // Action cameras / photo
  'action camera', 'action cam', 'insta360', 'gopro', 'φωτογραφική κάμερα',
  // Toys
  'lego', 'hoverboard', 'scooter', 'παιδικό παιχνίδι',
];

// ── System Prompt Builder ──
function buildKaizershopPrompt(categories: string[], concepts: string[]): string {
  return `You are the digital sales assistant for kaizershop.gr, a Greek technology & electronics e-commerce store. ALWAYS respond in Greek.

## HOW THE UI WORKS
The user sees a PRODUCT GRID next to the chat. The grid updates ONLY when you call searchProducts. If you don't call the tool, the user sees the SAME products — even if you talk about different ones.

## WHEN TO CALL searchProducts
ALWAYS call searchProducts when the user:
- Asks for products ("δείξε μου κινητά", "θέλω ακουστικά", "ψάχνω θήκη")
- Narrows/changes preferences ("Samsung", "μέχρι 200€", "bluetooth")
- Mentions a SPECIFIC price/budget ("μέχρι 50€", "30-50€", "πάνω από 100€")
- Says "ας δούμε", "δείξε μου", "τι έχετε σε..."

NEVER mention products (names/prices) without calling searchProducts IN THIS TURN — UNLESS you are recommending from previous results.

EXCEPTION — Do NOT call the tool for: greetings, thanks, general questions, clarifying questions.

### ABSOLUTE vs RELATIVE price requests
- **ABSOLUTE price** ("μέχρι 50€", "30-50€", "πάνω από 100€") → ALWAYS call searchProducts with price filters. Keep the SAME categories as the previous search.
- **RELATIVE price** ("φθηνό", "φθηνότερο", "πιο οικονομικό", "κάτι πιο ακριβό", "ακριβότερο") → Do NOT call searchProducts. Recommend from the previous results using priceRange and cheapestItems. If no higher-priced items exist in results, tell the user and offer to search other brands or a higher price range — but stay in the SAME product type/categories.

## CONTEXT MANAGEMENT
- New product type (e.g. phones → headphones) → FRESH search params. Do NOT inherit keywords/categories from previous searches.
- User picks from your clarification options (e.g. you offered "ακουστικά, smartwatch, ηχεία" → user says "smartwatch") → treat as a FOCUSED request. Use ONLY categories and keywords relevant to THAT specific product. Do NOT carry over context words from the original broad query.
- SAME product type refined (e.g. "κάτι ακριβότερο", "πιο ακριβό", "από Samsung", "μεγαλύτερη") → use the EXACT same categories AND searchTerm as the previous search. Only change filters (price/brand) or add new constraints. Example: you searched TVs with categories ["Σπίτι & Γραφείο > Τηλεοράσεις > Samsung"] → user says "κάτι πιο ακριβό" → keep the SAME TV categories, adjust price filter. Do NOT switch to phone categories just because "Samsung" is in context.
- CRITICAL: "πιο ακριβό", "φθηνότερο", "κάτι καλύτερο" are RELATIVE refinements — they refer to the SAME product type you just showed. NEVER search a different product type.
- Keep only EXPLICIT constraints: "Samsung", "μέχρι 200€" can carry over. Vague adjectives do NOT transfer.
- When unsure, ASK instead of guessing. One brief question is better than a noisy search.

## SALES CONSULTANT MINDSET
You are a knowledgeable electronics sales consultant — not just a search tool. Your job is to help the customer find the RIGHT product, not just ANY product. Think like a real salesperson in a tech store:

### IMPORTANT: You do NOT know exact stock, pricing, or availability per brand. NEVER suggest specific brands with price estimates (e.g. "Apple starts from 800€"). Instead, ask about budget and use case, then let the search results show what's actually available.

### ALWAYS ask clarifying questions for broad product types:
These products have critical specs that determine which model is right. Ask BEFORE searching:

**Τηλεόραση / TV:**
- "Τι μέγεθος χώρου έχετε; Για σαλόνι (55"+), κρεβατοκάμαρα (32-43"), ή κουζίνα (24-32");"
- "Τι budget έχετε κατά νου;"
- Do NOT suggest specific brands — let the search results show what's available.

**Κινητό / Smartphone:**
- "Τι budget έχεις κατά νου;"
- "Τι χρήση κυρίως; (φωτογραφίες, gaming, απλή χρήση)"
- Do NOT suggest specific brands with price estimates — you don't know exact stock/pricing. Let the search results show what's available.

**Ακουστικά / Headphones:**
- "Ενσύρματα ή ασύρματα bluetooth;"
- "Για αθλητισμό, καθημερινή χρήση, ή gaming;"
- "In-ear (earbuds) ή over-ear;"

**Laptop / PC:**
- "Για δουλειά, gaming, ή καθημερινή χρήση;"
- "Τι budget;"

**Smartwatch:**
- "Για ποιο κινητό; (Android ή iPhone)"
- "Κυρίως για fitness tracking ή γενική χρήση;"

**Δώρο:**
- "Για ποιον είναι; Τι ηλικία; Τι ενδιαφέροντα;"
- "Τι budget;"

### When to search directly (DO NOT ask):
- **Specific model**: "θέλω iPhone 16", "Xiaomi Redmi Note 15" → search immediately
- **Specific brand + type**: "ακουστικά JBL", "θήκη Samsung A54" → search immediately
- **Follow-ups**: "σε μαύρο", "μέχρι 50€", "από Samsung" → search immediately
- **User already gave specs**: "ακουστικά bluetooth μέχρι 50€" → search immediately (all info present)
- **User responding to your question**: user picks from your options → search immediately

### How to ask:
- Brief: 1-2 questions max per turn, with specific options
- Show domain knowledge: mention actual specs and brands from the catalog
- Don't interrogate: if user gives partial info, work with it and ask ONE follow-up
- Example flow:
  - User: "θέλω τηλεόραση" → Ask about size and budget
  - User: "για σαλόνι, μέχρι 500€" → Search immediately with size/price filters
  - User: "θέλω κινητό" → Ask about budget and brand
  - User: "Samsung" → Ask budget (economy A-series vs premium S-series)
  - User: "μέχρι 300€" → Search immediately

## PRODUCT TITLE NOTES
Product titles in this store are often in ENGLISH or mixed Greek/English. Many include technical specs:
- "APPLE IPHONE 16 128GB ULTRAMARINE EU" — brand, model, storage, color, region
- "XIAOMI REDMI NOTE 15 256GB 8GB 4G NFC DUAL BLACK" — full spec string
- "ΘΗΚΗ ΓΙΑ samsung galaxy a34 5g" — Greek prefix + English model
- "tempered glass 9h for apple iphone xs max" — English, lowercase

When searching, use the ENGLISH product name or brand as searchTerm (that's what appears in titles).
Greek terms like "θήκη", "ακουστικά", "κινητό" also work as they appear in some titles.

## searchProducts PARAMETERS

### searchTerm (1-3 words)
- Use PRODUCT NAME or BRAND + MODEL: "iphone 16", "jbl ηχείο", "θήκη samsung"
- For broad searches: "ακουστικά", "smartwatch", "tablet"
- NEVER use generic words alone: "δώρο", "κάτι", "προϊόν"

### categories (EXACT names from the list below, MAX 15)
- Pick the most relevant categories. Use EXACT names from the category list — including the " > " hierarchy.
- CRITICAL: Do NOT mix product categories with accessory categories. When user asks for a DEVICE (phone, tablet, laptop), use ONLY device categories (Κινητά Τηλέφωνα > ...). Do NOT include cases/accessories unless the user specifically asks for accessories.
  - "θέλω iphone" → ONLY ["Κινητά Τηλέφωνα > Apple"] — NOT cases
  - "θήκη iphone" → ONLY ["Αξεσουάρ > Θήκες > Apple"] — NOT phones
  - "iphone και θήκη" → ask which one first, or search separately
- Categories are TOP-LEVEL only (e.g. "Αξεσουάρ", "Κινητά Τηλέφωνα"). Sub-paths show in the sidebar automatically.
- CRITICAL: Do NOT mix product categories with accessory categories. "θέλω iphone" → ["Κινητά Τηλέφωνα"]. "θήκη iphone" → ["Αξεσουάρ"].
- Example: "ακουστικά bluetooth" → ["Αξεσουάρ"]
- Example: "θήκη iPhone" → ["Αξεσουάρ"]
- Example: "gaming" → ["Κονσόλες & gaming"]
- Example: "smartwatch" → ["Gadgets"]
- Example: "κινητό" → ["Κινητά Τηλέφωνα"]
- Example: "οθόνη" → ["Desktop & Laptop"]
- Example: "τηλεόραση" → ["Σπίτι & Γραφείο"]
- Example: "καφετιέρα" → ["Σπίτι & Γραφείο"]

### keywords (MANDATORY 3-5)
- Use brand names and product types that appear in titles
- Example: "ακουστικά" → ["jbl", "qcy", "ακουστικό", "earbuds", "headset"]
- Example: "θήκη iphone" → ["θήκη", "case", "silicone", "back cover"]
- Example: "gaming" → ["playstation", "nintendo", "controller", "headset"]

### filters (price, brand)
- Price: "μέχρι 30€" → ["price <= 30"]
- Brand: "από Apple" → ["brand: Apple"], "JBL" → ["brand: JBL"]
- Note: No color/material/size filters available for this client — specs are in titles only

## RESPONSES
- Concise (2-4 sentences)
- Markdown links for products: [name](url)
- Bullet points (-) for lists
- ALWAYS respond in Greek
- When products have specs in titles (storage, color, connectivity), mention the relevant specs when recommending

## SUGGESTIONS
At the END of your response, add when relevant:
[SUGGESTIONS: Ακουστικά, Smartwatches, Φορτιστές, Gaming]
- Use EXACT category names from the list below, max 6

## RESPONSES
- Concise (2-4 sentences)
- Do NOT say "Βρήκα X [product type]" using the total count — results include related products from other categories, so the count is misleading. Instead, use natural openers like "Βρήκα τα παρακάτω...", "Βρήκα επιλογές...", "Ορίστε μερικά...".
- When highlighting specific products, pick the 2-3 MOST RELEVANT to the user's query — not random items. For tech products, mention key specs (storage, RAM, connectivity) from the title.
- Markdown links for products: [name](url)
- Bullet points (-) for lists
- Ask clarifying questions when needed
- Respond in the language the user uses
- NEVER say "δεν υπάρχει" or "δεν βρέθηκε" when the search returned products. Scan ALL product titles carefully — the match may use a different naming convention.

## PRODUCT CATEGORIES (use ONLY these exact names)
${JSON.stringify(categories)}

## PRODUCT CONCEPTS (flexible — suggest beyond this list too)
${JSON.stringify(concepts)}
`;
}

// ── The Full Client Config ──

export const kaizershopConfig: ClientConfig = {
  // ── Identity ──
  clientId: 'kaizershop',
  storeName: 'kaizershop.gr',
  storeDescription: 'a Greek technology & electronics e-commerce store',

  // ── Features ──
  features: {
    hybridSearch: true,
    agentChat: true,
    addToCart: false,
    externalPricing: false,
    // Two-word queries that find nothing are retried word-by-word and offered
    // as labelled groups instead of an empty screen. This has been ON in the
    // production DB for a while but was MISSING here (found 2026-07-28), which
    // meant two things: local testing didn't reproduce live search behaviour,
    // and the file-fallback used when Supabase is unreachable would silently
    // drop the rescue in production. The runtime reads the DB row; this line is
    // the fallback truth and what a fresh provisioning would carry.
    wordSplitFallback: true,
  },

  // ── Embedding & Integration ──
  allowedOrigins: [],
  // Demo-hosted only (no embed anywhere) → all traffic is demo by definition.
  // ⚠️ At go-live the switch is a DIRECT DB WRITE to clients.production_origins (+
  // allowed_origins), NOT an edit here — the runtime reads config from Supabase and only
  // falls back to this file if the DB is unreachable. See "Runbook: Set Up a New Client"
  // step 8. (A brand-new client is different: provision-client.ts DOES carry this file's
  // value into the DB at birth.)
  productionOrigins: [],
  hostingStrategy: 'demo',
  cart: { strategy: 'navigate' },

  // ── Meilisearch ──
  indexName: 'products_kaizershop',
  embedderName: 'kaizershop-openai',
  locale: 'ell',

  // ── Feed ──
  // Switched 2026-06-04 from Facebook Atom feed to OpenCart full XML catalog.
  // The new feed exposes the full <filters> + <category_trees> + <manufacturer>
  // structure that's visible on the live kaizershop.gr sidebar.
  feedUrl: 'https://www.kaizershop.gr/full_catalog_xml.xml',
  productLimit: 5000,
  feedFormat: 'kaizershop_xml',

  // ── Dynamic Facet Fields ──
  // Sourced from <filter_group_name> in the XML's <filters> block, plus
  // `subcategory` synthesised from <category_trees> and `stock_status` from
  // the OpenCart label. `xmlFilterKey: null` = synthesised by the parser.
  // Sidebar shows only facets that have ≥1 value for the current result set
  // (per docs/search-engine/FACET_ARCHITECTURE.md), so listing many here is
  // safe — empty ones are hidden automatically.
  //
  // Extraction (query word → filter) is gated separately by `facetSkipStems`
  // in kaizershop.code.ts — currently only `color` is extractable (the rest
  // are sidebar-click filters per the SEARCH_SETUP_GUIDE.md §4.0 rubric).
  facetFields: [
    // ── Synthesised facets (not from <filters>) ──
    { field: 'subcategory',          label: 'Κατηγορία',                  xmlFilterKey: null, isArray: true },
    // NOTE: the `stock_status` facet (Διαθεσιμότητα) was removed 2026-06-09.
    // Availability is now judged by the numeric `quantity` field at index time
    // (only in-stock products are indexed), so every product shown is in stock
    // and a separate availability filter is unnecessary. See kaizershopXml.ts.

    // ── Primary user-facing facets ──
    { field: 'color',                label: 'Χρώμα',                       xmlFilterKey: 'Χρώμα',                       isArray: true },
    { field: 'product_type',         label: 'Είδος',                       xmlFilterKey: 'Είδος',                       isArray: true },
    { field: 'case_type',            label: 'Είδος Θήκης',                 xmlFilterKey: 'Είδος Θήκης',                 isArray: true },
    { field: 'features',             label: 'Δυνατότητες & Λειτουργίες',   xmlFilterKey: 'Δυνατότητες & Λειτουργίες',   isArray: true },
    { field: 'screen_size',          label: 'Μέγεθος Οθόνης',              xmlFilterKey: 'Μέγεθος Οθόνης',              isArray: true },
    { field: 'storage_capacity',     label: 'Χωρητικότητα',                xmlFilterKey: 'Χωρητικότητα',                isArray: true },
    { field: 'ram',                  label: 'Μνήμη RAM',                   xmlFilterKey: 'Μνήμη RAM',                   isArray: true },
    { field: 'connectivity',         label: 'Συνδεσιμότητα',               xmlFilterKey: 'Συνδεσιμότητα',               isArray: true },
    { field: 'os',                   label: 'Λειτουργικό',                 xmlFilterKey: 'Λειτουργικό',                 isArray: true },
    { field: 'sim',                  label: 'Sim',                         xmlFilterKey: 'Sim',                          isArray: true },
    { field: 'camera',               label: 'Camera',                      xmlFilterKey: 'Camera',                       isArray: true },
    { field: 'battery_capacity',     label: 'Χωρητικότητα Μπαταρίας',      xmlFilterKey: 'Χωρητικότητα Μπαταρίας',      isArray: true },
    { field: 'screen_protection',    label: 'Προστασία Οθόνης',            xmlFilterKey: 'Προστασία Οθόνης',            isArray: true },

    // ── Device-compatibility facets (per brand) ──
    { field: 'compatible_apple',     label: 'Αξεσουάρ Apple',              xmlFilterKey: 'Αξεσουάρ Apple',              isArray: true },
    { field: 'compatible_samsung',   label: 'Αξεσουάρ Samsung',            xmlFilterKey: 'Αξεσουάρ Samsung',            isArray: true },
    { field: 'compatible_xiaomi',    label: 'Αξεσουάρ Xiaomi',             xmlFilterKey: 'Αξεσουάρ Xiaomi',             isArray: true },
    { field: 'compatible_huawei',    label: 'Αξεσουάρ Huawei',             xmlFilterKey: 'Αξεσουάρ Huawei',             isArray: true },
    { field: 'compatible_honor',     label: 'Αξεσουάρ Honor',              xmlFilterKey: 'Αξεσουάρ Honor',              isArray: true },
    { field: 'compatible_nokia',     label: 'Αξεσουάρ Nokia',              xmlFilterKey: 'Αξεσουάρ Nokia',              isArray: true },
    { field: 'compatible_oneplus',   label: 'Αξεσουάρ OnePlus',            xmlFilterKey: 'Αξεσουάρ OnePlus',            isArray: true },
    { field: 'compatible_oppo',      label: 'Αξεσουάρ Oppo',               xmlFilterKey: 'Αξεσουάρ Oppo',               isArray: true },
    { field: 'compatible_realme',    label: 'Αξεσουάρ Realme',             xmlFilterKey: 'Αξεσουάρ Realme',             isArray: true },
    { field: 'compatible_console',   label: 'Συμβατή Κονσόλα',             xmlFilterKey: 'Συμβατή Κονσόλα',             isArray: true },
    { field: 'compatible_os',        label: 'Συμβατό Λειτουργικό',         xmlFilterKey: 'Συμβατό Λειτουργικό',         isArray: true },

    // ── TV / monitor specs ──
    { field: 'panel_type',           label: 'Τύπος Panel',                 xmlFilterKey: 'Τύπος Panel',                 isArray: true },
    { field: 'screen_resolution',    label: 'Ανάλυση Οθόνης',              xmlFilterKey: 'Ανάλυση Οθόνης',              isArray: true },

    // ── Cable / connection specs ──
    { field: 'connection_type',      label: 'Σύνδεση',                     xmlFilterKey: 'Σύνδεση',                     isArray: true },
    { field: 'cable',                label: 'Καλώδιο',                     xmlFilterKey: 'Καλώδιο',                     isArray: true },
    { field: 'cable_type',           label: 'Τύπος Καλώδιου',              xmlFilterKey: 'Τύπος Καλώδιου',              isArray: true },
    { field: 'usb_ports',            label: 'Αριθμός Θυρών USB',           xmlFilterKey: 'Αριθμός Θυρών USB',           isArray: true },
    { field: 'connections',          label: 'Συνδέσεις',                   xmlFilterKey: 'Συνδέσεις',                   isArray: true },
    { field: 'connection_speed',     label: 'Ταχύτητα Σύνδεσης',           xmlFilterKey: 'Ταχύτητα Σύνδεσης',           isArray: true },
    { field: 'frequency',            label: 'Συχνότητα',                   xmlFilterKey: 'Συχνότητα',                   isArray: true },
    { field: 'audio_channels',       label: 'Κανάλια',                     xmlFilterKey: 'Κανάλια',                     isArray: true },

    // ── CPU / PC build specs ──
    // NOTE: no `model` facet. The feed's "Μοντέλο" filter (present on ~19
    // phones) used to be mapped here — but `model` is the RESERVED pin-code
    // field (ui.popularProductCodes → `model IN [...]`), and the facet loop was
    // clobbering the feed's per-product <model> SKU (unique for ALL products)
    // with that near-empty array, making pinning unusable. Removed 2026-07-08;
    // DB row deleted the same day. The parser also guards `model` now.
    { field: 'cpu_cores',            label: 'Πυρήνες Επεξεργαστή',         xmlFilterKey: 'Πυρήνες Επεξεργαστή',         isArray: true },
    { field: 'cpu_family',           label: 'Οικογένεια',                  xmlFilterKey: 'Οικογένεια',                  isArray: true },
    { field: 'cpu_socket_amd',       label: 'Socket AMD',                  xmlFilterKey: 'Socket AMD',                  isArray: true },
    { field: 'cpu_socket_intel',     label: 'Socket Intel',                xmlFilterKey: 'Socket Intel',                isArray: true },
    { field: 'chipset_model',        label: 'Μοντέλο Chipset',             xmlFilterKey: 'Μοντέλο Chipset',             isArray: true },
    { field: 'ram_type',             label: 'Τύπος Μνήμης Ram',            xmlFilterKey: 'Τύπος Μνήμης Ram',            isArray: true },
    { field: 'memory_channels',      label: 'Memory Channels',             xmlFilterKey: 'Memory Channels',             isArray: true },
    { field: 'motherboard_size',     label: 'Μέγεθος Μητρικής',            xmlFilterKey: 'Μέγεθος Μητρικής',            isArray: true },
    { field: 'case_size',            label: 'Μέγεθος Κουτιού',             xmlFilterKey: 'Μέγεθος Κουτιού',             isArray: true },
    { field: 'cooling_type',         label: 'Είδος Ψύξης',                 xmlFilterKey: 'Είδος Ψύξης',                 isArray: true },
    { field: 'radiator_size',        label: 'Μέγεθος Ψυγείου',             xmlFilterKey: 'Μέγεθος Ψυγείου',             isArray: true },
    { field: 'fan_size',             label: 'Διάσταση Ανεμιστήρα (mm)',    xmlFilterKey: 'Διάσταση Ανεμιστήρα (mm)',    isArray: true },
    { field: 'drive_kind',           label: 'Είδος Δίσκου',                xmlFilterKey: 'Είδος Δίσκου',                isArray: true },
    { field: 'drive_type',           label: 'Τύπος Δίσκου',                xmlFilterKey: 'Τύπος Δίσκου',                isArray: true },
    { field: 'physical_size',        label: 'Μέγεθος',                     xmlFilterKey: 'Μέγεθος',                     isArray: true },

    // ── Peripherals (mouse / age etc.) ──
    { field: 'mouse_type',           label: 'Τύπος Ποντικιού',             xmlFilterKey: 'Τύπος Ποντικιού',             isArray: true },
    { field: 'mouse_sensor',         label: 'Αισθητήρας',                  xmlFilterKey: 'Αισθητήρας',                  isArray: true },
    { field: 'mouse_handedness',     label: 'Σχεδιασμός',                  xmlFilterKey: 'Σχεδιασμός',                  isArray: true },
    { field: 'age_group',            label: 'Ηλικία',                      xmlFilterKey: 'Ηλικία',                      isArray: true },
  ],

  // ── Search Tuning ──
  search: {
    semanticRatio: 0.0,
    minScoreThreshold: 0.65,  // Lower than parousiasi — English titles need more flexibility
    fallbackScoreThreshold: 0.75,
    maxResultsToFetch: 1000,
    maxResultsToReturn: 1000,
    enableWordSplitFallback: true,
    wordSplitFallbackThreshold: 1,  // triggers only when 0 results
  },

  // ── Agent Search Tuning ──
  agentSearch: {
    minScoreThreshold: 0.75,  // Lower for mixed-language titles
    layer4ScoreThreshold: 0.70,
    layer1: { limit: 100, matchingStrategy: 'last' },
    layer2: { maxCategories: 15, totalBudget: 80, minPerCategory: 12, matchingStrategy: 'last' },
    layer3: { limit: 20, maxKeywords: 5, minKeywordLength: 3, matchingStrategy: 'last' },
    layer4: { limit: 120, matchingStrategy: 'all' },
    discovery: { minTermLength: 3, matchingStrategy: 'last' },
    maxProductsToReturn: 200,
  },

  // ── Language ──
  language: {
    stopWords: [
      'ο', 'ι', 'το', 'τα', 'του', 'τις', 'τον',
      'κε', 'για', 'σε', 'με', 'απο', 'προς',
      'κατ', 'κατιτ',
    ],
    synonyms: {
      // Greek → English — ONLY when Greek word doesn't appear in titles/categories
      // Keys are NORMALIZED forms (output of normalizeGreek)
      // Rule: if the Greek word already matches via category/stemmer, DON'T add a synonym
      'ακουστ': ['earbuds', 'headset', 'headphones', 'airpods', 'bluetooth'],  // ακουστικά — titles use English names; +bluetooth per kaizershop team
      // NOTE: we deliberately do NOT add a reverse `airpods → [headset, headphones, ...]`
      // synonym. It broadens an "airpods" search to the full headphone set, but Meilisearch
      // ranks a synonym match equal to a literal one, so word-position bumped real Apple
      // AirPods down to ~position 10 (below Razer/Logitech HEADSETs) — violating the
      // "show the actual AirPods first" requirement. Searching "ακουστικά" already includes
      // AirPods via the forward mapping above; the closest-match fallback covers dead-ends.
      'θικ': ['case', 'cover'],                    // θήκη — titles use "case"/"cover"
      'πλικτρολογι': ['keyboard'],                  // πληκτρολόγιο — titles use "keyboard"
      'ποντ': ['mouse'],                            // ποντίκι — titles use "mouse"
      'οθον': ['monitor'],                          // οθόνη — titles use "monitor"
      'φορτιστ': ['charger'],                       // φορτιστής — titles use "charger"
      'χιριστιρι': ['controller', 'gamepad'],       // χειριστήριο — titles use "controller"
      'καλοδι': ['cable'],                          // καλώδιο — titles use "cable"
      'σκλιρ': ['hdd', 'ssd'],                     // σκληρός δίσκος — titles use "hdd"/"ssd"
      // English-only synonyms
      'playstation': ['ps5', 'ps4'],
      'ps5': ['playstation'],
      'laptop': ['notebook'],
      'τιλεορασ': ['tv'],                              // τηλεόραση
      'tv': ['τιλεορασ'],
      // Phones: κινητό/κινητά ↔ smartphone ↔ mobile. "κινητό" already matches the
      // "Κινητά Τηλέφωνα" category (normalized "κινιτ"); these also let the English
      // terms (which appear in ~17 "smartphone" / ~12 "mobile" titles) find phones,
      // and vice-versa. Greeklish "kinito"/"kinita" → κινιτ works via transliteration
      // and inherits these too.
      'κινιτ': ['smartphone', 'mobile', 'mobile phone'],   // κινητό/κινητά
      'smartphone': ['κινιτ', 'mobile'],
      'mobile': ['κινιτ', 'smartphone'],

      // ── Domain synonyms added 2026-06-04 (from kaizershop team) ──
      // Watches: singular ρολόι and plural ρολόγια stem DIFFERENTLY (ρολο / ρολι vs
      // ρολογ), so the singular can't inherit the plural's synonym — hence three keys.
      // Also bridge the no-space/space spelling (smartwatch ↔ smart watch).
      'ρολογ': ['smartwatch', 'smartwatches', 'smart watch', 'watch'],   // ρολόγια/ρολογια (plural)
      'ρολο': ['smartwatch', 'smartwatches', 'smart watch', 'watch'],    // ρολόι (accented singular)
      'ρολι': ['smartwatch', 'smartwatches', 'smart watch', 'watch'],    // ρολοι (unaccented singular)
      // Give "smartwatch" the SAME reach as "ρολοι" — most watches are titled with the
      // plain word "watch" (APPLE WATCH, SAMSUNG GALAXY WATCH), so without 'watch' here,
      // "smartwatch samsung" couldn't match them while "ρολοι samsung" could.
      'smartwatch': ['smart watch', 'smartwatches', 'watch'],
      'smart watch': ['smartwatch', 'smartwatches', 'watch'],
      'σκουπ': ['vacuum', 'vacuums'],                                // Σκούπα/Σκούπες → vacuums
      'smart σκουπ': ['vacuum', 'vacuums'],                          // Smart σκούπες → all vacuums
      'σκουπ ρομποτ': ['robot vacuum', 'xiaomi'],                    // Σκούπες ρομπότ → robot vacuums (Xiaomi)
      'σκουπ χορις καλοδι': ['cordless vacuum', 'xiaomi', 'dyson'],  // σκούπα χωρίς καλώδιο → cordless (Xiaomi & Dyson)
      'καμερ': ['action cam', 'action camera', 'camera', 'instax'],  // Κάμερα → action cams + camera + Fujifilm Instax instant cameras
      'φοτογραφ καμερ': ['action cam', 'action camera'],             // Φωτογραφική κάμερα → action cams
      'ιχια': ['speakers', 'speaker'],                               // Ηχεία (plural) → speakers
      'ιχιο': ['speakers', 'speaker'],                               // Ηχείο (singular) → speakers
      // Helper synonyms — make the multi-word intents above actually fire by
      // bridging the per-word Greek↔Latin mapping. Without these, with the
      // basic search route's matchingStrategy:"all", "σκούπες ρομπότ" returns
      // 0 because "ρομπότ" doesn't lexically match "ROBOT" in titles.
      'ρομποτ': ['robot'],                                           // Greek ρομπότ → English robot (titles use ROBOT)
      'ασυρματ': ['wireless', 'cordless'],                           // ασύρματος → wireless/cordless
      'χορις καλοδι': ['cordless', 'wireless'],                      // χωρίς καλώδιο → cordless

      // ── Domain synonyms added 2026-06-04 (round 2) — titles use English ──
      'φριτεζ': ['air fryer'],                                       // Φριτέζα/Φριτέζες → air fryer
      'airfryer': ['air fryer'],                                     // "airfryer" (no space) → "air fryer" (titles spell it with a space). Regular synonym (single-word key) — scores ~0.95, survives the threshold; NOT a phraseSynonym (that's only for multi-word queries that score too low).
      'σκουτερ': ['scooter', 'scooters'],                            // Σκούτερ → scooter(s)
      'πατιν': ['scooter', 'scooters'],                              // Πατίνι/Πατίνια → scooter(s) — both normalize to "πατιν"; titles use English "SCOOTER" (e-scooters)
      'εσπρεσσ': ['espresso', 'espresso machine', 'espresso machines'],  // Εσπρεσσο (double-σ spelling)
      'εσπρεσ': ['espresso', 'espresso machine', 'espresso machines'],   // εσπρεσο (single-σ spelling)
      'καφετιερ': ['espresso', 'espresso machine', 'espresso machines'], // Καφετιέρα/Καφετιέρες → espresso machines
      'ανεμιστιρ': ['fan'],                                          // Ανεμιστήρας → fan
    },
    // ── Synonym SOURCES (natural language, dashboard display/edit layer) ──
    // The words as a merchant would type them. The dashboard edits THIS and
    // derives the engine `synonyms` map from it on save: keys = normalizeChain
    // (every single-pass-reachable form, so singular AND plural fire), values
    // normalized. The hand-authored `synonyms` map above remains the legacy
    // bootstrap; the DB is authoritative post-dashboard (migration 2026-07-08
    // regenerated it from these sources — scripts/migrate-synonym-sources.ts).
    // NOTE: this fixed dead keys the hand-authored map had (e.g. 'ασυρματ' can
    // never match — queries normalize υ→ι — the derived 'ασιρματ' can).
    synonymSources: {
      'ακουστικά': ['earbuds', 'headset', 'headphones', 'airpods', 'bluetooth'],
      'θήκη': ['case', 'cover'],
      'θήκες': ['case', 'cover'],
      'πληκτρολόγιο': ['keyboard'],
      'ποντίκι': ['mouse'],
      'οθόνη': ['monitor'],
      'φορτιστής': ['charger'],
      'χειριστήριο': ['controller', 'gamepad'],
      'καλώδιο': ['cable'],
      'σκληρός': ['hdd', 'ssd'],
      'playstation': ['ps5', 'ps4'],
      'ps5': ['playstation'],
      'laptop': ['notebook'],
      'τηλεόραση': ['tv'],
      'tv': ['τηλεόραση'],
      'κινητό': ['smartphone', 'mobile', 'mobile phone'],
      'smartphone': ['κινητό', 'mobile'],
      'mobile': ['κινητό', 'smartphone'],
      'ρολόγια': ['smartwatch', 'smartwatches', 'smart watch', 'watch'],
      'ρολόι': ['smartwatch', 'smartwatches', 'smart watch', 'watch'],
      'ρολοι': ['smartwatch', 'smartwatches', 'smart watch', 'watch'],
      'smartwatch': ['smart watch', 'smartwatches', 'watch'],
      'smart watch': ['smartwatch', 'smartwatches', 'watch'],
      'σκούπα': ['vacuum', 'vacuums'],
      'smart σκούπες': ['vacuum', 'vacuums'],
      'σκούπες ρομπότ': ['robot vacuum', 'xiaomi'],
      'σκούπα χωρίς καλώδιο': ['cordless vacuum', 'xiaomi', 'dyson'],
      'κάμερα': ['action cam', 'action camera', 'camera', 'instax'],
      'φωτογραφική κάμερα': ['action cam', 'action camera'],
      'ηχεία': ['speakers', 'speaker'],
      'ηχείο': ['speakers', 'speaker'],
      'ρομπότ': ['robot'],
      'ασύρματος': ['wireless', 'cordless'],
      'χωρίς καλώδιο': ['cordless', 'wireless'],
      'φριτέζα': ['air fryer'],
      'airfryer': ['air fryer'],
      'σκούτερ': ['scooter', 'scooters'],
      'πατίνι': ['scooter', 'scooters'],
      'εσπρέσσο': ['espresso', 'espresso machine', 'espresso machines'],
      'εσπρέσο': ['espresso', 'espresso machine', 'espresso machines'],
      'καφετιέρα': ['espresso', 'espresso machine', 'espresso machines'],
      'ανεμιστήρας': ['fan'],
    },
    // ── Phrase synonyms (multi-word query expansion) ──
    // When the user's query EQUALS one of these phrases, the search route ALSO runs a
    // search for the mapped term(s) and merges the results. Why not a Meilisearch
    // synonym? A multi-word Meili synonym DOES fire, but its matches score ~0.36
    // (synonym, not literal) and get cut by minScoreThreshold 0.65. Running a real
    // search for "airpods" instead → literal matches score ~0.95 → survive the threshold
    // and rank first. Applied with filters too, so facet counts stay consistent. Keys are
    // matched accent/typo-insensitively (normalizeGreek). This is the "1-word ↔ multi-word
    // synonym" concept (future client dashboard will expose it as such).
    phraseSynonyms: {
      'ακουστικα iphone': ['airpods'],
      'iphone ακουστικα': ['airpods'],
    },
    // ── Greeklish keep-words (smart Greeklish) ──
    // English/brand model words that appear in TITLES and must NOT be transliterated
    // when they show up inside a Greeklish query (so "thiki iphone" keeps "iphone",
    // "kinito galaxy" keeps "galaxy"). BRAND NAMES (samsung, apple, xiaomi, jbl, qcy…)
    // are auto-detected from the `brand` facet, so this list only needs NON-brand model/
    // product words. Anything missing here is caught by the try-both fallback (slower).
    // See lib/smartGreeklish.ts + docs/KAIZERSHOP_GO_LIVE_PLAN.md §"Smart Greeklish".
    greeklishKeepWords: [
      'iphone', 'ipad', 'ipod', 'macbook', 'imac', 'airpods', 'airtag', 'magsafe',
      'galaxy', 'buds', 'redmi', 'poco', 'watch',
      'tablet', 'gaming', 'wireless', 'bluetooth', 'tempered', 'glass',
      'headset', 'headphones', 'earbuds', 'soundbar', 'monitor', 'keyboard', 'scooter',
    ],
    queryStopWords: [
      'από', 'απο', 'με', 'για', 'σε', 'και', 'στο', 'στη', 'στα',
      'στον', 'στην', 'του', 'της', 'των', 'τον', 'την', 'τους', 'τις',
      'ο', 'η', 'το', 'οι', 'τα', 'ένα', 'ενα', 'μια', 'μία', 'ένας', 'ενας', 'προς',
    ],
    pricePatterns: {
      maxPrice: /(?:μέχρι|μεχρι|έως|εως|κάτω από|κατω απο|κατω|κάτω|up to|under|below)\s+(\d+(\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
      minPrice: /(?:πάνω από|πανω απο|πανω|πάνω|από|απο|over|above)\s+(\d+(\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
      range: /(?:από|απο|from)?\s*(\d+(?:\.\d+)?)\s*(?:-|–|έως|εως|μέχρι|μεχρι|to)\s*(\d+(?:\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
    },
    noisyPhrases: [],  // No noisy phrases identified yet for this client
  },

  // ── Index Settings ──
  indexSettings: {
    searchableAttributes: [
      'title_short',             // First 4 words — matches here rank highest
      'title',
      'title_normalized',
      'brand',
      'category',
      'category_normalized',
      'subcategory',
      'subcategory_normalized',
      'id',
      'description_short',
      'description_short_normalized',
    ],
    displayedAttributes: [
      'id', 'client_id', 'title', 'description', 'description_short',
      'category', 'subcategory', 'brand', 'price', 'image_url', 'url',
    ],
    rankingRules: [
      'words', 'typo', 'proximity', 'attribute', 'sort', 'exactness',
    ],
    localizedAttributes: [
      {
        locales: ['ell'],
        attributePatterns: ['title', 'category', 'description_short'],
      },
    ],
    documentTemplate: '{{doc.category}} - {{doc.brand}} {{doc.title}}. {{doc.description}}',
    faceting: {
      maxValuesPerFacet: 500,
      sortFacetValuesBy: { '*': 'count' as const },
    },
    // Pinned to Meilisearch's default (5/9) — which is what this index has
    // ACTUALLY been running all along. This block was never sent to the engine
    // until 2026-08-25 (see applyIndexSettings), so the 3/7 written here
    // had never taken effect. Wiring it up would have silently made this client
    // MORE typo-tolerant than it has ever been, untested. Pinning to 5/9 keeps
    // behaviour byte-identical. Tune it deliberately, with a measured battery,
    // the way syfak's 6/10 was — do not copy syfak's number blindly.
typoTolerance: {
      minWordSizeForTypos: { oneTypo: 5, twoTypos: 9 },  // Lower thresholds for English words
    },
  },

  // ── System Prompt ──
  systemPrompt: {
    categories: PRODUCT_CATEGORIES,
    productConcepts: PRODUCT_CONCEPTS,
    buildPrompt: buildKaizershopPrompt,
  },

  // ── UI Configuration ──
  ui: {
    // ✨ Corrected 2026-09-22 to their EXACT css values, read from
    // kaizershop.gr rather than eyedropped: both were one digit off
    // (#27aae2→#28aae2, #a4ce39→#a5ce39). On their own site both are
    // BACKGROUND fills on the footer blocks, with white text on them — which is
    // why `brandTextColor` stays unset here and the default white is correct.
    // kaizershop is a genuine TWO-colour brand; it is the only client whose
    // hover is a different hue rather than a darker shade of its own brand.
    brandColor: '#28aae2',      // Blue — kaizershop brand color (their exact)
    brandColorHover: '#a5ce39', // Green — kaizershop hover color (their exact)
    // ⚠️ THE ONLY PLACE THIS INVENTED COLOUR SURVIVES, and only because neither
    // of their real colours can be read as TEXT on a white product title:
    //     their blue  #28aae2   2.65:1
    //     their green #a5ce39   1.83:1
    //     this        #2a86cc   3.90:1
    // A matched word is TEXT, so it needs the text threshold, and 3.90:1 is the
    // best available without abandoning their hue entirely. Everything else
    // uses their own colours — founder's call 2026-09-22.
    highlightColor: '#2a86cc',  // Darker blue — matched text ONLY
    // Their own brand blue, per the same call: the scrollbar is a surface, not
    // text, so it follows the brand rather than the derived colour. Note this
    // is 2.65:1 against white where a UI element wants 3:1 — a 6px bar in their
    // real blue, accepted deliberately over a colour they do not use.
    scrollbarColor: '#28aae2',
    productTitleColor: '#000000',
    fontCssVariable: undefined,  // Use system default font
    backgroundImage: undefined,  // No background image
    categoriesToHide: new Set([
      'Hot Offers',
      'Εκθεσιακά',
      'Μεταχειρισμένα',
    ]),
    exampleQueries: [
      { emoji: '📱', text: 'Κινητό μέχρι 300 ευρώ', description: 'Budget phone' },
      { emoji: '🎧', text: 'Ασύρματα ακουστικά bluetooth', description: 'Wireless earbuds' },
      { emoji: '🎮', text: 'Gaming αξεσουάρ για PlayStation', description: 'Gaming gear' },
      { emoji: '⌚', text: 'Smartwatch για γυμναστική', description: 'Fitness watch' },
      { emoji: '☕', text: 'Καφετιέρα espresso για το σπίτι', description: 'Coffee machine' },
    ],
    topSearches: ['iphone', 'samsung', 'airpods', 'θήκη', 'ακουστικά'],
    strings: {
      searchPlaceholder: [
        'Ακουστικά bluetooth μέχρι 50€',
        'iPhone 16 θήκη',
        'Gaming ποντίκι',
        'Smartwatch Xiaomi',
      ],
      brandLabel: 'Κατασκευαστής',
      filtersTitle: 'Φίλτρα',
      suggestedCategories: 'Προτεινόμενες Κατηγορίες',
      otherCategories: 'Άλλες Κατηγορίες',
      priceRange: 'Εύρος Τιμής',
      productsTitle: 'Προϊόντα',
      noResults: 'Δε βρέθηκαν προϊόντα',
      noResultsHint: 'Δοκιμάστε διαφορετικές λέξεις-κλειδιά ή πιο γενικούς όρους αναζήτησης',
      showLess: 'Λιγότερα',
      showMore: (n: number) => `+${n} ακόμα...`,
      popularProducts: 'Δημοφιλή Προϊόντα',
      searchingSuggestions: 'Αναζήτηση Προτάσεων',
      searchingProducts: 'Αναζήτηση προϊόντων...',
      addToCart: 'ΑΓΟΡΑΣΕ ΤΟ!',
      topSearchesTitle: 'Δημοφιλείς Αναζητήσεις',
      aiAssistantTitle: 'AI Βοηθός',
      aiAssistantGreeting: 'Πώς μπορώ να σε βοηθήσω; Ρώτησέ με για προϊόντα, δώρα ή οτιδήποτε χρειάζεσαι.',
    },
  },
};
